import { HeaderPositionType, PaletteColorType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class ChartCategory extends BaseCategory {
    get name() {
        return "Chart";
    }

    get type() {
        return SlideCategories.CHART;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for visualizing data trends, comparisons, or patterns with line charts, bar charts, column charts, or area charts. Don't use for pie charts.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a chart slide based on the user's prompt.
        Provide the chart data including categories (x-axis labels) and one or more data series.
        Specify the chart type: line, column, bar, or area.`;
    }

    getPromptSchema() {
        return {
            chartType: "type of chart: line, column, bar, or area",
            categories: ["array of x-axis category labels (e.g., months, years, product names)"],
            series: [
                {
                    name: "name of the data series",
                    data: ["array of numeric values corresponding to each category"]
                }
            ],
            xAxisTitle: "optional title for the x-axis",
            yAxisTitle: "optional title for the y-axis",
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.series) || !this.validateArray(model.categories)) {
            return false;
        }
        return true;
    }

    getImagePrompt() {
        return false;
    }

    getSlideModels(model, options = {}) {
        const chartType = model.chartType || "line";

        let models = [
            // Build the requested chart type first
            this.createChart(model, chartType),
        ];

        // Add variations for other chart types
        const chartTypes = ["line", "column", "bar", "area", "pie"];
        for (const type of chartTypes) {
            if (type !== chartType) {
                models.push(this.createChart(model, type));
            }
        }

        return models.filter(m => m != null);
    }

    createChart(model, chartType) {
        if (chartType == "pie") {
            return this.buildPieChart(model);
        } else {
            return this.buildChart(model, chartType);
        }
    }

    buildChart(model, chartType) {
        let builder = new SlideModelBuilder("dashboard", model);
        builder.setBackgroundColor(PaletteColorType.BACKGROUND_LIGHT);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });
        const categories = model.categories || ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
        const series = model.series || [
            {
                name: "Data",
                data: [10, 40, 50, 30, 70, 50]
            }
        ];

        // Build series data with proper structure
        const chartSeries = series.map((s, index) => {
            const seriesIndex = index + 1;
            return {
                id: `series${seriesIndex}`,
                name: s.name || `Series ${seriesIndex}`,
                type: chartType,
                data: s.data.map(y => ({ y: y, pt: true })),
                colorName: `chart${seriesIndex}`,
                marker: "none",
                zones: [
                    {
                        style: "default",
                    }
                ]
            };
        });

        const chartData = {
            series: chartSeries,
            xAxis: {
                visible: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                categories: categories,
                dateFormatting: "none",
                showAxisLine: true,
                showGridLine: false,
                showMajorTicks: false,
                showMinorTicks: false,
                // axisTitle: model.xAxisTitle ? "top" : "none",
                axisTitle: "none",
                axisTitleText: model.xAxisTitle || "Title",
                labelInterval: 1,
                type: "linear",
                autoType: "linear",
                categoryType: "auto",
                labels: {
                    enabled: true,
                },
                zeroAxisPadding: false
            },
            yAxis: {
                visible: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                showAxisLine: true,
                showGridLines: true,
                showMajorTicks: false,
                showMinorTicks: false,
                labelAbbreviation: false,
                labelCurrency: "none",
                labelFormat: "ones",
                axisTitle: model.yAxisTitle ? "edge" : "none",
                axisTitleText: model.yAxisTitle || "Value",
                labels: {
                    enabled: true
                },
                format: "number",
                formatOptions: {
                    decimal: 0,
                    separator: true,
                    abbreviated: false,
                    changeStyle: "none",
                    changeColor: true,
                    currency: "$",
                    dateFormat: "MMM Do",
                    accountingStyle: false
                }
            },
            yAxis2: {
                visible: false,
                opposite: true,
                showFirstLabel: true,
                showLastLabel: true,
                endOnTick: true,
                showAxisLine: true,
                showGridLines: true,
                showMajorTicks: false,
                showMinorTicks: false,
                labelAbbreviation: false,
                labelCurrency: "none",
                labelFormat: "ones",
                axisTitle: "none",
                axisTitleText: "Title",
                labels: {
                    enabled: true
                },
                format: "number",
                formatOptions: {
                    decimal: 0,
                    separator: true,
                    abbreviated: false,
                    changeStyle: "none",
                    changeColor: true,
                    currency: "$",
                    dateFormat: "MMM Do",
                    accountingStyle: false
                }
            },
            chartAnnotations: {
                items: [],
                connections: {
                    items: []
                }
            }
        };

        builder.createPrimary({
            fullBleed: false,
            showGutter: true,
            cellColor: "none",
            gridLayout: {
                rows: 12,
                cols: 12,
                cells: [{
                    x: 0, y: 0, width: 12, height: 12
                }]
            },
            items: [{
                componentType: "Chart",
                cellColor: "none",
                legendPosition: series.length > 1 ? "bottom" : "off",
                chartData: chartData
            }]
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    buildPieChart(model) {
        if (model.series.length > 1 || (model.series[0] && model.series[0].data.length > 8)) {
            // Pie charts are not suitable for multiple series or too many items
            return null;
        }
        let builder = new SlideModelBuilder("dashboard", model);

        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.LEFT });

        builder.createPrimary({
            items: [{
                componentType: "PieChart",
                childElement: {
                    collectionColor: "theme-to-dark",
                    data: model.categories.map((category, index) => ({
                        value: model.series[0].data[index],
                        label: category
                    })),
                    format: "percent",
                    innerRadius: 0,
                    isDonut: false,
                    labelFormat: "percentage",
                    sortValues: true
                }
            }]
        });
        return builder.model;
    }
}

